import { isLocalHost, isTestStand } from "../constants/common";
import {
  buildAnalyticsUserContext,
  getCalculationAnalyticsProperties,
  getMarketListingAnalyticsProperties,
  AnalyticsUserContext,
} from "./context";
import {
  ANALYTICS_EVENTS,
  AnalyticsEventName,
  AnalyticsPrimitive,
  AnalyticsProperties,
} from "./events";
import {
  AnalyticsAdapter,
  yandexMetrikaAdapter,
} from "./adapters/yandexMetrika";

export {
  ANALYTICS_EVENTS,
  buildAnalyticsUserContext,
  getCalculationAnalyticsProperties,
  getMarketListingAnalyticsProperties,
};
export type {
  AnalyticsEventName,
  AnalyticsProperties,
  AnalyticsUserContext,
};

const SCHEMA_VERSION = 1;
const PRIVATE_KEYS = new Set([
  "email",
  "phone",
  "telephone",
  "client_name",
  "contact_name",
  "first_name",
  "last_name",
  "full_name",
]);

const analyticsEnabled =
  process.env.REACT_APP_ANALYTICS_ENABLED !== "false" &&
  !isLocalHost &&
  !isTestStand;

let currentUserContext: AnalyticsUserContext = {
  actor_type: "anonymous",
};
let lastIdentifiedUserId: string | number | undefined;
let adapters: AnalyticsAdapter[] = [yandexMetrikaAdapter];

const createEventId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const environment = isLocalHost
  ? "local"
  : isTestStand
    ? "stage"
    : "production";

const sanitize = (properties: AnalyticsProperties): AnalyticsProperties =>
  Object.entries(properties).reduce<AnalyticsProperties>(
    (result, [key, value]) => {
      if (
        value === undefined ||
        PRIVATE_KEYS.has(key.toLowerCase()) ||
        (typeof value === "number" && !Number.isFinite(value))
      ) {
        return result;
      }

      result[key] = Array.isArray(value)
        ? value.filter(
            (item): item is AnalyticsPrimitive =>
              item === null ||
              typeof item === "string" ||
              typeof item === "number" ||
              typeof item === "boolean",
          )
        : value;
      return result;
    },
    {},
  );

const basePayload = (
  eventName: string,
  properties: AnalyticsProperties,
): AnalyticsProperties =>
  sanitize({
    event_name: eventName,
    event_id: createEventId(),
    event_schema_version: SCHEMA_VERSION,
    event_occurred_at: new Date().toISOString(),
    event_source: "web",
    app_environment: environment,
    app_version: process.env.REACT_APP_VERSION || "0.1.0",
    app_build: process.env.REACT_APP_GIT_BRANCH,
    page_path:
      typeof window === "undefined"
        ? undefined
        : `${window.location.pathname}${window.location.search}`,
    ...currentUserContext,
    ...properties,
  });

export const registerAnalyticsAdapter = (adapter: AnalyticsAdapter) => {
  if (!adapters.includes(adapter)) {
    adapters = [...adapters, adapter];
  }
};

export const initAnalytics = () => {
  if (!analyticsEnabled) {
    return;
  }

  adapters.forEach((adapter) => adapter.init());
};

export const identifyAnalyticsUser = (
  user?: unknown,
  userData?: unknown,
): AnalyticsUserContext => {
  currentUserContext = buildAnalyticsUserContext(user, userData);

  if (!analyticsEnabled || currentUserContext.actor_user_id === undefined) {
    return currentUserContext;
  }

  const userId = currentUserContext.actor_user_id;
  if (lastIdentifiedUserId !== userId) {
    lastIdentifiedUserId = userId;
  }

  adapters.forEach((adapter) => adapter.identify(userId, currentUserContext));
  return currentUserContext;
};

export const clearAnalyticsUser = () => {
  currentUserContext = { actor_type: "anonymous" };
  lastIdentifiedUserId = undefined;
  if (analyticsEnabled) {
    adapters.forEach((adapter) => adapter.clearIdentity());
  }
};

export const getAnalyticsContextSnapshot = (): AnalyticsUserContext => ({
  ...currentUserContext,
});

export const createShareAnalyticsSnapshot = (): AnalyticsProperties => ({
  analytics_schema_version: SCHEMA_VERSION,
  analytics_created_at: new Date().toISOString(),
  ...getAnalyticsContextSnapshot(),
});

export const trackPageView = (
  url: string,
  options: {
    title?: string;
    referer?: string;
    properties?: AnalyticsProperties;
  } = {},
) => {
  if (!analyticsEnabled) {
    return;
  }

  const params = basePayload("page_view", options.properties || {});
  adapters.forEach((adapter) =>
    adapter.pageView(url, {
      title: options.title,
      referer: options.referer,
      params,
    }),
  );
};

export const trackAnalyticsEvent = (
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {},
) => {
  if (!analyticsEnabled) {
    return;
  }

  const payload = basePayload(eventName, properties);
  adapters.forEach((adapter) => adapter.track(eventName, payload));
};

export const isAnalyticsEnabled = analyticsEnabled;
